import { parse as parseYaml } from 'yaml';
import { buildNullPredictions } from '../loaders/altogether/synthesizePredictions';
import { parseNewPredictionsBlob } from '../loaders/image/parser';
import type { NewKeypointRaw, NewPredictionsFile } from '../types';
import type { SecPoseCamera } from './loadAssets';

export interface LegacySeedCameraResult {
  predictions: NewPredictionsFile;
  seededFrames: number[];
}

export interface LegacySeedResult {
  cameras: Partial<Record<SecPoseCamera, LegacySeedCameraResult>>;
  warnings: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasHumanStatuses(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const frames = value.frames;
  if (!isRecord(frames)) return false;
  for (const frame of Object.values(frames)) {
    if (!isRecord(frame)) continue;
    for (const keypoint of Object.values(frame)) {
      if (!isRecord(keypoint)) continue;
      if (keypoint.status === 'manual_annotated' || keypoint.status === 'occluded') return true;
    }
  }
  return false;
}

function frameKeyByIndex(frames: Record<string, unknown>): Map<number, string> {
  const out = new Map<number, string>();
  for (const key of Object.keys(frames)) {
    const index = Number(key);
    if (!Number.isInteger(index) || index < 0 || out.has(index)) continue;
    out.set(index, key);
  }
  return out;
}

export async function buildLegacyV2Seeds(
  toonText: string,
  frameIdsByCamera: Record<SecPoseCamera, number[]>
): Promise<LegacySeedResult> {
  const warnings: string[] = [];
  let root: Record<string, unknown>;
  try {
    const parsed = parseYaml(toonText);
    if (!isRecord(parsed)) {
      return { cameras: {}, warnings: ['legacy_toon_root_not_mapping'] };
    }
    root = parsed;
  } catch {
    return { cameras: {}, warnings: ['legacy_toon_invalid_yaml'] };
  }

  const cameras: Partial<Record<SecPoseCamera, LegacySeedCameraResult>> = {};
  for (const camera of Object.keys(frameIdsByCamera) as SecPoseCamera[]) {
    const source = root[camera];
    if (!isRecord(source)) continue;
    if (hasHumanStatuses(source)) {
      warnings.push(`legacy_toon_human_status:${camera}`);
      continue;
    }

    try {
      const parsed = await parseNewPredictionsBlob(
        new Blob([JSON.stringify(source)], { type: 'application/json' })
      );
      const sourceFrames = parsed.raw.frames as Record<string, unknown>;
      const sourceFrameKeys = frameKeyByIndex(sourceFrames);
      const frameIds = frameIdsByCamera[camera];
      const normalized = buildNullPredictions(frameIds, parsed.meta.imageSize, parsed.meta.fps);
      const seededFrames: number[] = [];

      for (const frameId of frameIds) {
        if (!sourceFrameKeys.has(frameId)) continue;
        const records = parsed.frameMap[frameId];
        if (!records) continue;
        const frame: Record<string, NewKeypointRaw> = {};
        for (const record of records) {
          const inFrame = record.defaultVisibility === 'visible';
          frame[record.name] = {
            pixel_coords: inFrame ? [record.xy[0], record.xy[1]] : [-1, -1],
            confidence_score: record.score,
            status: inFrame ? null : 'out_of_frame',
          };
        }
        normalized.frames[String(frameId).padStart(6, '0')] = frame;
        seededFrames.push(frameId);
      }

      if (seededFrames.length > 0) {
        cameras[camera] = { predictions: normalized, seededFrames };
      }
    } catch {
      warnings.push(`legacy_toon_camera_invalid:${camera}`);
    }
  }

  return { cameras, warnings };
}
